import React, { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LuLayoutDashboard, LuBuilding2, LuUsers, LuCar,
  LuCalendarCheck, LuMenu, LuLogOut, LuX, LuMapPin,
  LuPanelLeftClose, LuPanelLeftOpen, LuUser, LuChevronDown, LuNavigation, LuSettings
} from 'react-icons/lu';

const navItems = [
  { path: '/dashboard', icon: LuLayoutDashboard, label: 'Dashboard', roles: ['superadmin', 'company'] },
  { path: '/companies',  icon: LuBuilding2,      label: 'Hajj Companies',  roles: ['superadmin'] },
  { path: '/agents',     icon: LuUsers,          label: 'Hajj Agents',  roles: ['superadmin', 'company'] },
  { path: '/drivers',    icon: LuCar,            label: 'Drivers',    roles: ['superadmin'] },
  { path: '/trips',      icon: LuCalendarCheck,  label: 'Trips',      roles: ['superadmin', 'company'] },
  { path: '/locations',  icon: LuMapPin,         label: 'Locations',  roles: ['superadmin'] },
  { path: '/routes',     icon: LuNavigation,     label: 'Routes',     roles: ['superadmin'] },
  { path: '/settings',   icon: LuSettings,       label: 'Settings',   roles: ['superadmin'] },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    const handle = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };
  const filteredNav = navItems.filter(n => n.roles.includes(user?.role));

  const Sidebar = ({ isMobile = false }) => (
    <aside className={`bg-gradient-to-b from-hajj-dark to-[#0f2420] flex flex-col h-full transition-all duration-300 ${isMobile ? 'w-64' : collapsed ? 'w-[68px]' : 'w-64'}`}>
      {/* Logo */}
      <div className={`flex items-center border-b border-white/10 px-4 h-[60px] flex-shrink-0 ${collapsed && !isMobile ? 'justify-center' : 'justify-between'}`}>
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-hajj-gold to-[#b8922a] flex items-center justify-center text-white text-xl shadow-gold flex-shrink-0">
            <LuMapPin />
          </div>
          {(!collapsed || isMobile) && (
            <div className="min-w-0">
              <div className="text-white font-bold text-sm leading-tight">الموجه الآلي</div>
              <div className="text-white/40 text-[10px] uppercase tracking-widest">Hajj Management</div>
            </div>
          )}
        </div>
        {/* Mobile close */}
        {isMobile && (
          <button className="text-white/40 text-xl ml-2" onClick={() => setMobileOpen(false)}><LuX /></button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2 mt-4 flex flex-col gap-0.5 overflow-y-auto">
        {filteredNav.map(item => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={() => isMobile && setMobileOpen(false)}
            title={collapsed && !isMobile ? item.label : undefined}
            className={({ isActive }) =>
              `flex items-center px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${collapsed && !isMobile ? 'justify-center' : 'gap-3'} ${
                isActive
                  ? 'bg-hajj-mid text-white'
                  : 'text-white/60 hover:bg-white/8 hover:text-white'
              }`
            }
          >
            <item.icon className="text-lg flex-shrink-0" />
            {(!collapsed || isMobile) && <span>{item.label}</span>}
          </NavLink>
        ))}
      </nav>

    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-white">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex flex-shrink-0">
        <Sidebar />
      </div>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
          <div className="fixed left-0 top-0 h-full z-50 flex lg:hidden">
            <Sidebar isMobile />
          </div>
        </>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="h-[60px] bg-white border-b border-gray-100 flex items-center justify-between px-6 flex-shrink-0 shadow-hajj-sm">
          {/* Mobile menu open */}
          <button className="text-xl text-hajj-dark lg:hidden p-1" onClick={() => setMobileOpen(true)}>
            <LuMenu />
          </button>
          {/* Desktop collapse toggle */}
          <button
            onClick={() => setCollapsed(c => !c)}
            className="hidden lg:flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:bg-hajj-cream hover:text-hajj-dark transition-all duration-200"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <LuPanelLeftOpen className="text-base" /> : <LuPanelLeftClose className="text-base" />}
          </button>
          {/* Profile dropdown */}
          <div className="relative" ref={dropRef}>
            <button
              onClick={() => setDropOpen(o => !o)}
              className="flex items-center gap-2.5 pl-1 pr-2 py-1 rounded-xl hover:bg-hajj-cream transition-colors"
            >
              {user?.photo ? (
                <img src={user.photo} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 border-2 border-hajj-gold/30" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-hajj-gold to-hajj-teal flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {user?.name?.[0]?.toUpperCase()}
                </div>
              )}
              <div className="hidden sm:block text-left">
                <div className="text-sm font-semibold text-hajj-dark leading-tight">{user?.name}</div>
                <div className="text-xs text-gray-400 leading-tight">{user?.email}</div>
              </div>
              <LuChevronDown className={`hidden sm:block text-gray-400 text-sm transition-transform duration-200 ${dropOpen ? 'rotate-180' : ''}`} />
            </button>

            {dropOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl border border-gray-100 shadow-hajj-md py-1 z-50">
                <button
                  onClick={() => { setDropOpen(false); navigate('/profile'); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-hajj-dark hover:bg-hajj-cream transition-colors"
                >
                  <LuUser className="text-base text-hajj-teal" />
                  Profile
                </button>
                <div className="border-t border-gray-100 my-1" />
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors"
                >
                  <LuLogOut className="text-base" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
